import { useMemo, useState } from 'react';
import { useVirtualAppointmentTypeLabels } from '../../hooks/useVirtualAppointments';
import {
    MAX_PLANNED_VIRTUAL_APPOINTMENTS,
    type OpenVirtualAppointmentsNowResult,
    type VirtualAppointmentDto
} from '../../services/virtualAppointments';
import type { OpenNowSubmitButton } from './OpenNowFooter';
import { dueVaName, openNowDueNotice, selectDueOnSubmit } from './openNowDue';
import { openNowSubmitLabel, openNowSubmitState } from './openNowSubmit';
import {
    countOpenNowModes,
    listedDraft,
    openNowDraftToPlan,
    validateOpenNowDraft,
    type OpenNowDraft,
    type OpenNowDraftPatch,
    type OpenNowPatientContext
} from './openNowDraft';
import type { VirtualAppointmentListTab } from './VirtualAppointmentListTabs';
import type { VirtualAppointmentListEntry } from './VirtualAppointmentSelectorList';
import { useOpenNowDrafts } from './useOpenNowDrafts';
import type { OpenVirtualAppointmentsNow } from './useOpenVirtualAppointmentsNow';
import { useVirtualAppointmentSelection } from './useVirtualAppointmentSelection';
import { useVirtualAppointmentSummaries } from './useVirtualAppointmentSummaries';
import { hasDraftErrors, type VirtualAppointmentDraftErrors } from './virtualAppointmentDraft';

export type OpenNowTab = 'open' | 'planned' | 'new';

export type OpenNowErrorsByKey = Record<string, VirtualAppointmentDraftErrors>;

const NO_DUE_IDS: ReadonlySet<string> = new Set();

// The patient's saved VAs, listed locked and read-only.
function useLockedEntries(
    vas: VirtualAppointmentDto[],
    dueIds: ReadonlySet<string> = NO_DUE_IDS
): VirtualAppointmentListEntry[] {
    const summaries = useVirtualAppointmentSummaries(vas);
    return useMemo(
        () =>
            summaries.map((summary) => ({
                summary,
                hasErrors: false,
                removable: false,
                due: dueIds.has(summary.key)
            })),
        [summaries, dueIds]
    );
}

interface PlannedWithDue {
    entries: VirtualAppointmentListEntry[];
    due: VirtualAppointmentDto[];
    dueNames: string[];
}

// Submitting runs the trigger, which also opens the planned VAs already due: flagged and named here.
function usePlannedWithDue(planned: VirtualAppointmentDto[], canOpenNow: boolean): PlannedWithDue {
    const due = useMemo(() => selectDueOnSubmit(planned, canOpenNow), [planned, canOpenNow]);
    const dueIds = useMemo(() => new Set(due.map((va) => va.id)), [due]);
    const entries = useLockedEntries(planned, dueIds);
    const dueNames = useMemo(
        () => entries.filter((e) => e.due).map((e) => dueVaName(e.summary)),
        [entries]
    );
    return { entries, due, dueNames };
}

interface DraftEntriesInput {
    drafts: OpenNowDraft[];
    errorsByKey: OpenNowErrorsByKey;
    revealErrors: boolean;
}

// The new drafts, each removable like in the RDI/SDI wizard; an empty list just can't submit.
function useDraftEntries(input: DraftEntriesInput): VirtualAppointmentListEntry[] {
    const { drafts, errorsByKey, revealErrors } = input;
    const typeLabels = useVirtualAppointmentTypeLabels();
    const listed = useMemo(() => drafts.map(listedDraft), [drafts]);
    const summaries = useVirtualAppointmentSummaries(NO_SAVED, listed, typeLabels);
    return useMemo(
        () =>
            summaries.map((summary) => ({
                summary,
                hasErrors: revealErrors && hasDraftErrors(errorsByKey[summary.key] ?? {}),
                removable: true
            })),
        [summaries, errorsByKey, revealErrors]
    );
}

const NO_SAVED: VirtualAppointmentDto[] = [];

function keysOf(entries: VirtualAppointmentListEntry[]): string[] {
    return entries.map((e) => e.summary.key);
}

export interface OpenNowFormInput {
    allowMoveOnly: boolean;
    openVas: VirtualAppointmentDto[];
    planned: VirtualAppointmentDto[];
    patient: OpenNowPatientContext;
    submitter: OpenVirtualAppointmentsNow;
    onOpened: (result: OpenVirtualAppointmentsNowResult) => void;
}

export interface OpenNowForm {
    drafts: OpenNowDraft[];
    update: (key: string, patch: OpenNowDraftPatch) => void;
    remove: (key: string) => void;
    tab: OpenNowTab;
    setTab: (tab: OpenNowTab) => void;
    tabs: VirtualAppointmentListTab<OpenNowTab>[];
    /** The active tab's rows and selection. */
    entries: VirtualAppointmentListEntry[];
    selectedKey: string | null;
    select: (key: string) => void;
    errorsByKey: OpenNowErrorsByKey;
    revealErrors: boolean;
    canAdd: boolean;
    handleAdd: () => void;
    submit: OpenNowSubmitButton;
    /** Names the planned VAs a submit also opens; null when none is due or nothing can submit. */
    dueNotice: string | null;
}

// Starts with no drafts; with move-only allowed and VAs already open, submitting empty moves on.
export function useOpenNowForm({
    allowMoveOnly,
    openVas,
    planned,
    patient,
    submitter,
    onOpened
}: OpenNowFormInput): OpenNowForm {
    const { drafts, add, update, remove } = useOpenNowDrafts();
    const [revealErrors, setRevealErrors] = useState(false);
    const { treatmentStartDate, dmLinked, canOpenNow } = patient;
    // The modal rebuilds `aligners` each render, so the memo keys on its fields instead.
    const { current: currentAligner, total: totalAligners } = patient.aligners;
    const errorsByKey = useMemo<OpenNowErrorsByKey>(() => {
        const aligners = { current: currentAligner, total: totalAligners };
        return Object.fromEntries(
            drafts.map((d) => [
                d.key,
                validateOpenNowDraft(d, { treatmentStartDate, dmLinked, canOpenNow, aligners })
            ])
        );
    }, [drafts, treatmentStartDate, dmLinked, canOpenNow, currentAligner, totalAligners]);
    const invalid = drafts.some((d) => hasDraftErrors(errorsByKey[d.key] ?? {}));
    const { entries: plannedEntries, due, dueNames } = usePlannedWithDue(planned, canOpenNow);
    const counts = countOpenNowModes(drafts);
    const { canSubmit, moveTarget } = openNowSubmitState({
        counts,
        open: openVas,
        allowMoveOnly,
        canOpenNow,
        due
    });
    const dueNotice = openNowDueNotice(dueNames, canSubmit);
    const openEntries = useLockedEntries(openVas);
    const draftEntries = useDraftEntries({ drafts, errorsByKey, revealErrors });
    const [tab, setTab] = useState<OpenNowTab>('new');
    const openSelection = useVirtualAppointmentSelection(keysOf(openEntries));
    const plannedSelection = useVirtualAppointmentSelection(keysOf(plannedEntries));
    const draftSelection = useVirtualAppointmentSelection(
        keysOf(draftEntries),
        drafts[0]?.key ?? null
    );
    const active = { open: openSelection, planned: plannedSelection, new: draftSelection }[tab];
    const entries = { open: openEntries, planned: plannedEntries, new: draftEntries }[tab];
    const tabs: VirtualAppointmentListTab<OpenNowTab>[] = [
        { key: 'new', label: 'New VAs', count: drafts.length },
        { key: 'open', label: 'Open VAs', count: openVas.length },
        { key: 'planned', label: 'Planned VAs', count: planned.length }
    ];
    const { submitting, open } = submitter;

    const handleAdd = () => {
        const key = add();
        if (key === null) return;
        setTab('new');
        draftSelection.select(key);
    };
    const handleSubmit = () => {
        setRevealErrors(true);
        if (invalid || submitting || !canSubmit) return;
        open(drafts.map(openNowDraftToPlan), onOpened);
    };
    const submit: OpenNowSubmitButton = {
        label: openNowSubmitLabel({
            counts,
            moveTarget,
            submitting,
            canOpenNow,
            dueCount: due.length
        }),
        disabled: submitting || !canSubmit || (revealErrors && invalid),
        onClick: handleSubmit
    };
    const canAdd = drafts.length < MAX_PLANNED_VIRTUAL_APPOINTMENTS;
    return {
        drafts,
        update,
        remove,
        tab,
        setTab,
        tabs,
        entries,
        selectedKey: active.selectedKey,
        select: active.select,
        errorsByKey,
        revealErrors,
        canAdd,
        handleAdd,
        submit,
        dueNotice
    };
}
